import { useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  Maximize2,
  SkipForward,
  FastForward,
  } from "lucide-react";

import { stepData, tabsList } from "../../demoData";
import type { QuestionnaireSubmitPayload, StepId } from "../../types";

export function QuestionnaireWidget({
  onSubmitSuccess,
  isAlreadySubmitted = false,
}: {
  onSubmitSuccess: (payload: QuestionnaireSubmitPayload) => void;
  isAlreadySubmitted?: boolean;
}) {
  const [isFullscreen, setIsFullscreen] = useState(!isAlreadySubmitted);
  const [activeTab, setActiveTab] = useState<StepId>("market");
  const [answers, setAnswers] = useState<Record<StepId, string[]>>({
    market: [],
    risk: [],
    return: [],
    interpret: [],
    constraints: [],
  });
  const [customInputs, setCustomInputs] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(isAlreadySubmitted);

  const currentTabIndex = tabsList.findIndex((t) => t.id === activeTab);
  const isLastTab = currentTabIndex === tabsList.length - 1;
  const currentData = stepData[activeTab];
  const hasSelection = answers[activeTab].length > 0;

  const handleOptionToggle = (optionId: string) => {
    setAnswers((prev) => {
      const currentAnswers = prev[activeTab];
      if (currentData.type === "single") {
        return {
          ...prev,
          [activeTab]: currentAnswers.includes(optionId) ? [] : [optionId],
        };
      }

      if (currentAnswers.includes(optionId)) {
        return {
          ...prev,
          [activeTab]: currentAnswers.filter((id) => id !== optionId),
        };
      }

      return { ...prev, [activeTab]: [...currentAnswers, optionId] };
    });
  };

  const handleNext = () => {
    if (isLastTab) {
      setIsSubmitted(true);
      if (isFullscreen) setIsFullscreen(false);
      const payload: QuestionnaireSubmitPayload = {
        answers,
        customInputs,
      };
      setTimeout(() => onSubmitSuccess(payload), 500);
      return;
    }
    setActiveTab(tabsList[currentTabIndex + 1].id);
  };

  const handleSkip = () => {
    const newAnswers: Record<StepId, string[]> = { ...answers };
    
    // 自动勾选所有未作答标签页的第一个选项
    tabsList.forEach((tab) => {
      if (newAnswers[tab.id].length === 0) {
        const firstOption = stepData[tab.id].options[0];
        if (firstOption) {
          newAnswers[tab.id] = [firstOption.id];
        }
      }
    });
    
    setAnswers(newAnswers);
    
    // 跳转到最后一个标签页并等待用户确认
    setActiveTab(tabsList[tabsList.length - 1].id);
  };

  const handleFastForward = () => {
    const newAnswers: Record<StepId, string[]> = { ...answers };
    if (newAnswers.market.length === 0) newAnswers.market = ["us_daily"];
    if (newAnswers.risk.length === 0) newAnswers.risk = ["risk_high"];
    if (newAnswers.return.length === 0) newAnswers.return = ["ret_high"];
    if (newAnswers.interpret.length === 0) {
      const first = stepData.interpret.options[0];
      if (first) newAnswers.interpret = [first.id];
    }
    if (newAnswers.constraints.length === 0) {
      const first = stepData.constraints.options[0];
      if (first) newAnswers.constraints = [first.id];
    }
    setAnswers(newAnswers);
    setActiveTab(tabsList[tabsList.length - 1].id);
  };

  if (isSubmitted) {
    return (
      <div className="border border-green-200 bg-green-50 rounded-xl p-5 flex items-center gap-4 text-green-800 anim-zoom-in mb-4">
        <CheckCircle2 className="text-green-600 shrink-0" size={24} />
        <div>
          <h4 className="font-semibold text-sm mb-0.5">Preferences Saved</h4>
          <p className="text-sm text-green-700/80">
            {isAlreadySubmitted
              ? "Strategy parameters confirmed."
              : "Received full parameters. Transitioning to flowchart builder..."}
          </p>
        </div>
      </div>
    );
  }

  const innerContent = (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <div className="flex overflow-x-auto border-b border-gray-200 bg-gray-50/50 scrollbar-hide pt-1 shrink-0">
        {tabsList.map((tab) => {
          const isCompleted = answers[tab.id].length > 0;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors relative flex items-center gap-2 ${
                activeTab === tab.id
                  ? "text-blue-600 bg-white"
                  : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              {isCompleted && activeTab !== tab.id && (
                <Check size={14} className="text-green-500" />
              )}
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600" />
              )}
            </button>
          );
        })}
      </div>

      <div className="p-6 flex-1 overflow-y-auto relative min-h-0">
        <div key={activeTab} className="anim-slide-right">
          <h3 className="text-[15px] font-semibold text-gray-800 mb-4">
            {currentData.title}
            {currentData.type === "multiple" && (
              <span className="text-gray-400 font-normal ml-2 text-sm">
                (select all that apply)
              </span>
            )}
          </h3>

          <div className="space-y-3">
            {currentData.options.map((opt) => {
              const isSelected = answers[activeTab].includes(opt.id);
              const isCustom = opt.id.startsWith("custom");

              return (
                <div key={opt.id} className="relative">
                  <label
                    className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      isSelected
                        ? "border-blue-500 bg-blue-50/30"
                        : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="pt-0.5 shrink-0">
                      <div
                        className={`w-5 h-5 flex items-center justify-center border transition-colors ${
                          currentData.type === "single"
                            ? "rounded-full"
                            : "rounded"
                        } ${
                          isSelected
                            ? "bg-blue-500 border-blue-500"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={isSelected}
                        onChange={() => handleOptionToggle(opt.id)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-sm font-medium ${
                          isSelected ? "text-blue-900" : "text-gray-800"
                        }`}
                      >
                        {opt.title}
                      </div>
                      {!isCustom && (
                        <div className="text-sm text-gray-500 mt-1">
                          {opt.description}
                        </div>
                      )}

                      {isCustom && isSelected && (
                        <input
                          type="text"
                          autoFocus
                          placeholder="Please specify..."
                          value={customInputs[opt.id] || ""}
                          onChange={(e) =>
                            setCustomInputs({
                              ...customInputs,
                              [opt.id]: e.target.value,
                            })
                          }
                          className="mt-3 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                          onClick={(e) => e.preventDefault()}
                        />
                      )}
                    </div>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 bg-white flex justify-between items-center shrink-0">
        <div className="text-xs text-gray-400 font-medium">
          Step {currentTabIndex + 1} of {tabsList.length}
        </div>
        <button
          onClick={handleNext}
          disabled={!hasSelection}
          className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            hasSelection
              ? "bg-black text-white hover:bg-gray-800 shadow-md translate-y-0"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          {isLastTab ? "Submit Preferences" : "Next Step"}
          {!isLastTab && <ChevronRight size={16} />}
        </button>
      </div>
    </div>
  );

  if (isFullscreen) {
    const fullscreenUI = (
      <div className="fixed inset-0 z-[9999] flex flex-col bg-gray-900/40 backdrop-blur-sm anim-fade-in">
        <div className="flex-1 bg-[#f9fafb] w-full flex flex-col anim-slide-up shadow-xl relative z-10 overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0 shadow-sm">
            <button
              onClick={() => setIsFullscreen(false)}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-900 py-1 px-2 rounded-md hover:bg-gray-100"
            >
              <ArrowLeft size={18} />{" "}
              <span className="text-sm font-medium">Back to Chat</span>
            </button>
            <div className="text-sm font-semibold text-gray-800">Strategy Setup</div>
            <div className="relative group/skip">
              <button
                onClick={handleSkip}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-900 py-1 px-2 rounded-md hover:bg-gray-100"
                title="Skip"
              >
                <SkipForward size={18} />
                <span className="text-sm font-medium">Skip</span>
              </button>
              <div className="absolute top-full right-0 z-50 pt-1 opacity-0 group-hover/skip:opacity-100 transition-opacity pointer-events-none group-hover/skip:pointer-events-auto">
                <button
                  onClick={handleFastForward}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-md shadow-lg whitespace-nowrap"
                >
                  <FastForward size={13} />
                  Skip (high-risk, high-reward)
                </button>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-hidden p-4 md:p-6 flex justify-center relative">
            <div className="w-full max-w-3xl bg-white border border-gray-200 rounded-2xl shadow-sm flex flex-col overflow-hidden h-full">
              {innerContent}
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <>
        <div className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-6 mt-4 mb-4 w-full flex flex-col items-center justify-center gap-3 anim-zoom-in">
          <Maximize2 size={24} className="text-gray-400" />
          <div className="text-sm font-medium text-gray-600">
            Completing questionnaire in fullscreen
          </div>
          <button
            onClick={() => setIsFullscreen(false)}
            className="text-sm text-blue-600 font-medium px-4 py-1.5 rounded-md hover:bg-blue-50"
          >
            Return to window
          </button>
        </div>
        {typeof document !== "undefined" && document.body
          ? createPortal(fullscreenUI, document.body)
          : fullscreenUI}
      </>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden mt-4 mb-4 w-full relative anim-zoom-in flex flex-col h-[500px]">
      <div className="absolute top-2.5 right-2 flex gap-1 z-10">
        <div className="relative group/skip">
          <button
            onClick={handleSkip}
            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-md flex items-center gap-1"
            title="Skip"
          >
            <SkipForward size={16} />
          </button>
          <div className="absolute top-full right-0 z-50 pt-1 opacity-0 group-hover/skip:opacity-100 transition-opacity pointer-events-none group-hover/skip:pointer-events-auto">
            <button
              onClick={handleFastForward}
              className="p-1.5 text-amber-500 hover:bg-amber-100 rounded-md shadow-lg"
              title="Fast-forward: Skip (high-risk, high-reward)"
            >
              <FastForward size={16} />
            </button>
          </div>
        </div>
        <button
          onClick={() => setIsFullscreen(true)}
          className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-md"
        >
          <Maximize2 size={16} />
        </button>
      </div>
      {innerContent}
    </div>
  );
}
